package org.example.project.game

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.database.database
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*

/**
 * MultiplayerGameViewModel — vollstaendige App-Paritaet.
 *
 * Spielregeln (1:1 wie Android-MainActivity):
 *
 *  - 1-4 aktive Spieler in /players. 4+ = Spectator (kann nichts tun).
 *  - Pro Zug 3 Aktionen erlaubt, jede unabhaengig:
 *      * 1 Hint-Button (-2 Punkte) → setzt hintUsed=true
 *      * 1 Joker/Thunder (-5 Punkte) → setzt jokerUsed=true
 *      * 1 Spalten-Antwort (+10 wenn richtig, +0 wenn falsch) → setzt answerUsed=true
 *  - Falsche Antwort ODER Skip-Button => Zugwechsel + Flags reset
 *  - Richtige Antwort => bleibt am Zug (kann weitere Aktionen)
 *  - Final richtig => +50, winner gesetzt
 *  - Final falsch => Zugwechsel
 *  - Punkte werden lokal sowohl fuer eigene als auch fremde Aktionen
 *    addiert (siehe prevRevealed/prevSolved Diff)
 *  - Aufgeben: jeder Spieler stimmt via giveUpVotes ab. Erst wenn ALLE
 *    aktiven Spieler zugestimmt haben, wird das Spiel beendet (alle Loesungen
 *    aufgedeckt, kein Gewinner).
 *  - Wenn Spieler den Raum verlaesst und gerade dran war, wird whosTurn
 *    automatisch an verbleibende Spieler weitergegeben (siehe MainActivity).
 */
class MultiplayerGameViewModel(
    initialRoomCode: String,
    private val isHost: Boolean,
    private val playerName: String,
    private val playerAvatar: String,
    private val langCode: String,
    private val difficulty: String
) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
    private val db = Firebase.database("https://assoziation-c5cda-default-rtdb.firebaseio.com")
    private val roomsRef = db.reference("rooms")
    private val levelLoader = LevelLoader()

    val playerId: String = getOrCreatePlayerId()
    private val _roomCode = MutableStateFlow(initialRoomCode)
    val roomCode = _roomCode.asStateFlow()

    private val _state = MutableStateFlow<GameState?>(null)
    val state = _state.asStateFlow()

    private val _isLoading = MutableStateFlow(true)
    val isLoading = _isLoading.asStateFlow()

    private val _errorMessage = MutableStateFlow<String?>(null)
    val errorMessage = _errorMessage.asStateFlow()

    // Phase-Anzeige fuer UI
    private val _phase = MutableStateFlow("waiting_for_guest")
    val phase = _phase.asStateFlow()

    private val _isMyTurn = MutableStateFlow(false)
    val isMyTurn = _isMyTurn.asStateFlow()

    private val _isSpectator = MutableStateFlow(false)
    val isSpectator = _isSpectator.asStateFlow()

    private val _hintUsed = MutableStateFlow(false)
    val hintUsed = _hintUsed.asStateFlow()

    private val _jokerUsed = MutableStateFlow(false)
    val jokerUsed = _jokerUsed.asStateFlow()

    private val _answerUsed = MutableStateFlow(false)
    val answerUsed = _answerUsed.asStateFlow()

    // Listen aller Spieler/Mitspieler
    private val _players = MutableStateFlow<List<String>>(emptyList())
    val players = _players.asStateFlow()

    private val _playerInfoMap = MutableStateFlow<Map<String, PlayerInfo>>(emptyMap())
    val playerInfoMap = _playerInfoMap.asStateFlow()

    private val _scoresMap = MutableStateFlow<Map<String, Int>>(emptyMap())
    val scoresMap = _scoresMap.asStateFlow()

    private val _winner = MutableStateFlow("")
    val winner = _winner.asStateFlow()

    private val _giveUpVotes = MutableStateFlow<Set<String>>(emptySet())
    val giveUpVotes = _giveUpVotes.asStateFlow()

    private val _isGameFinished = MutableStateFlow(false)
    val isGameFinished = _isGameFinished.asStateFlow()

    // Final-Solution-Logik
    private val _finalWrongCount = MutableStateFlow(0)
    val finalWrongCount = _finalWrongCount.asStateFlow()

    private val _isFinalHinted = MutableStateFlow(false)
    val isFinalHinted = _isFinalHinted.asStateFlow()

    private val _isAllRevealed = MutableStateFlow(false)
    val isAllRevealed = _isAllRevealed.asStateFlow()

    // ── interne State ──
    private var currentLangPack: LangPack? = null
    private var lastGameId: String? = null
    private var lastRevealedHints: Set<String> = emptySet()
    private var lastSolvedColumns: Set<String> = emptySet()
    private var listenerJob: Job? = null

    init {
        startListening(initialRoomCode)
    }

    // ───────────────────────────────────────────────────────────
    // Listener
    // ───────────────────────────────────────────────────────────

    private fun startListening(code: String) {
        listenerJob?.cancel()
        listenerJob = scope.launch {
            try {
                roomsRef.child(code).valueEvents.collect { snapshot ->
                    if (!snapshot.exists) {
                        _errorMessage.value = "Raum wurde geschlossen"
                        _isLoading.value = false
                        return@collect
                    }
                    val room = snapshot.value(GameRoom.serializer())
                    handleRoomUpdate(room)
                }
            } catch (e: Exception) {
                _errorMessage.value = "Verbindungsfehler: ${e.message}"
                _isLoading.value = false
            }
        }
    }

    private suspend fun handleRoomUpdate(room: GameRoom) {
        _players.value = room.players.keys.toList()
        _playerInfoMap.value = room.playerInfo
        _scoresMap.value = room.scores
        _winner.value = room.winner
        _giveUpVotes.value = room.giveUpVotes.keys
        _isSpectator.value = room.spectators.containsKey(playerId)
        _isGameFinished.value = room.winner.isNotEmpty()

        // Per-Zug-Flags nur fuer eigenen Zug uebernehmen, sonst lokal resetten
        if (room.whosTurn == playerId) {
            _hintUsed.value = room.hintUsed
            _jokerUsed.value = room.jokerUsed
            _answerUsed.value = room.answerUsed
        } else {
            _hintUsed.value = false
            _jokerUsed.value = false
            _answerUsed.value = false
        }

        _isMyTurn.value = (room.whosTurn == playerId) && !_isGameFinished.value && !_isSpectator.value

        // Phase fuer UI
        _phase.value = when {
            room.players.size < 2 -> "waiting_for_guest"
            room.winner.isNotEmpty() -> "finished"
            else -> "playing"
        }

        // Aufgeben: alle aktiven Spieler haben abgestimmt?
        if (room.giveUpVotes.size >= room.players.size && room.players.isNotEmpty() && !_isGameFinished.value) {
            // Alle haben aufgegeben -> Spiel beenden, alles enthuellen
            _isGameFinished.value = true
            // Lokal alle Spalten als geloest markieren fuer Anzeige
            currentLangPack?.let { pack ->
                _state.value = buildGameState(
                    pack = pack,
                    revealedHints = (1..4).flatMap { i -> listOf("A$i","B$i","C$i","D$i") }.toSet(),
                    solvedColumns = setOf("A","B","C","D"),
                    forceShowSolutions = true,
                    myScore = room.scores[playerId] ?: 0
                )
            }
            return
        }

        // Auto-skip wenn Zugspieler den Raum verlassen hat
        val activePlayers = room.players.keys.toList()
        if (!_isGameFinished.value
            && room.whosTurn.isNotEmpty()
            && activePlayers.isNotEmpty()
            && !activePlayers.contains(room.whosTurn)
            && playerId == activePlayers[0]
        ) {
            roomsRef.child(_roomCode.value).updateChildren(
                mapOf(
                    "whosTurn" to activePlayers[0],
                    "hintUsed" to false,
                    "jokerUsed" to false,
                    "answerUsed" to false
                )
            )
            return
        }

        // Level laden wenn neue gameId
        if (room.gameId.isNotEmpty() && room.gameId != lastGameId) {
            lastGameId = room.gameId
            try {
                currentLangPack = levelLoader.loadLevel(room.gameId, langCode)
                if (currentLangPack == null) {
                    _errorMessage.value = "Level konnte nicht geladen werden"
                    _isLoading.value = false
                    return
                }
            } catch (e: Exception) {
                _errorMessage.value = "Level-Fehler: ${e.message}"
                _isLoading.value = false
                return
            }
        }

        val pack = currentLangPack ?: return

        val newRevealed = room.revealedHints.keys
        val newSolved = room.solvedColumns.keys

        // Beim ersten Update keine Diff-Punkte vergeben (initiales Sync)
        if (lastGameId == room.gameId && lastRevealedHints.isNotEmpty()) {
            // (Diff-Punkte sind in der App so, dass JEDER Spieler Punkte fuer
            //  jede Aktion bekommt - egal wer sie ausgefuehrt hat. Fuer den
            //  eigenen Spieler haben wir die Punkte bereits beim Klicken
            //  in 'scores/{playerId}' geschrieben. Fuer fremde Aktionen
            //  schreiben wir KEINE Punkte hier - das wuerde doppelt zaehlen.
            //  Punkte erscheinen ohnehin via scores-Map im UI.)
        }

        lastRevealedHints = newRevealed
        lastSolvedColumns = newSolved

        // _isAllRevealed: alle 16 Hint-Buttons aufgedeckt?
        val allHintKeys = listOf("A","B","C","D").flatMap { c -> (1..4).map { "$c$it" } }
        _isAllRevealed.value = allHintKeys.all { newRevealed.contains(it) }

        _state.value = buildGameState(
            pack = pack,
            revealedHints = newRevealed,
            solvedColumns = newSolved,
            forceShowSolutions = room.winner.isNotEmpty(),
            myScore = room.scores[playerId] ?: 0
        )
        _isLoading.value = false
    }

    private fun buildGameState(
        pack: LangPack,
        revealedHints: Set<String>,
        solvedColumns: Set<String>,
        forceShowSolutions: Boolean,
        myScore: Int
    ): GameState {
        val cols = pack.toColumns().map { col ->
            val isSolved = solvedColumns.contains(col.id) || forceShowSolutions
            val isHinted = revealedHints.contains("TIP_${col.id}")
            col.copy(
                isSolved = isSolved,
                isHinted = isHinted,
                fields = col.fields.map { f ->
                    val revealed = revealedHints.contains(f.id) || isSolved
                    f.copy(isRevealed = revealed)
                }
            )
        }
        return GameState(
            columns = cols,
            finalSolution = pack.finalSolution,
            isGameWon = forceShowSolutions,
            isSurrendered = false, // wird via giveUpVotes/winner separat angezeigt
            currentScore = myScore
        )
    }

    // ───────────────────────────────────────────────────────────
    // Spieleraktionen — schreiben exakt die App-Felder
    // ───────────────────────────────────────────────────────────

    /**
     * Hint-Button (z.B. A1) — kostet -2, setzt hintUsed=true.
     * 1x pro Zug, bleibt am Zug.
     */
    fun revealHint(columnId: String, fieldId: String) {
        if (_isSpectator.value || !_isMyTurn.value || _hintUsed.value || _isGameFinished.value) return
        val current = _state.value ?: return
        val column = current.columns.find { it.id == columnId } ?: return
        val field = column.fields.find { it.id == fieldId } ?: return
        if (field.isRevealed || column.isSolved) return

        scope.launch {
            try {
                val updates = mapOf(
                    "revealedHints/$fieldId" to true,
                    "hintUsed" to true
                )
                roomsRef.child(_roomCode.value).updateChildren(updates)
                addOwnScore(-2)
            } catch (_: Exception) {}
        }
    }

    /**
     * Thunder/Joker (TIP_A etc.) — kostet -5, setzt jokerUsed=true.
     * 1x pro Zug, bleibt am Zug.
     */
    fun useThunder(columnId: String) {
        if (_isSpectator.value || !_isMyTurn.value || _jokerUsed.value || _isGameFinished.value) return
        val current = _state.value ?: return
        val column = current.columns.find { it.id == columnId } ?: return
        if (column.isSolved || column.isHinted) return

        scope.launch {
            try {
                val updates = mapOf(
                    "revealedHints/TIP_$columnId" to true,
                    "jokerUsed" to true
                )
                roomsRef.child(_roomCode.value).updateChildren(updates)
                addOwnScore(-5)
            } catch (_: Exception) {}
        }
    }

    /**
     * Spalte raten — richtig: +10, alle Hints/Tip dieser Spalte werden
     * enthuellt, bleibt am Zug. Falsch: Zugwechsel.
     */
    fun guessColumn(columnId: String, guess: String): Boolean {
        if (_isSpectator.value || !_isMyTurn.value || _answerUsed.value || _isGameFinished.value) return false
        val current = _state.value ?: return false
        val column = current.columns.find { it.id == columnId } ?: return false
        if (column.isSolved) return false

        val correct = column.columnSolution.equals(guess.trim(), ignoreCase = true)
        scope.launch {
            try {
                if (correct) {
                    val updates = mutableMapOf<String, Any>(
                        "solvedColumns/$columnId" to true,
                        "revealedHints/TIP_$columnId" to true,
                        "answerUsed" to true
                    )
                    for (i in 1..4) updates["revealedHints/$columnId$i"] = true
                    roomsRef.child(_roomCode.value).updateChildren(updates)
                    addOwnScore(10)
                } else {
                    // Falsch -> Zugwechsel
                    switchTurn()
                }
            } catch (_: Exception) {}
        }
        return correct
    }

    /**
     * Final-Solution. Richtig: +50, winner gesetzt. Falsch: Zugwechsel.
     */
    fun guessFinal(guess: String): Boolean {
        if (_isSpectator.value || !_isMyTurn.value || _isGameFinished.value) return false
        val current = _state.value ?: return false

        val correct = current.finalSolution.equals(guess.trim(), ignoreCase = true)
        scope.launch {
            try {
                if (correct) {
                    addOwnScore(50)
                    roomsRef.child(_roomCode.value).child("winner").setValue(playerId)
                } else {
                    _finalWrongCount.value = _finalWrongCount.value + 1
                    val allColsSolved = listOf("A","B","C","D").all { lastSolvedColumns.contains(it) }
                    if (_finalWrongCount.value >= 2 && allColsSolved) {
                        // Final-Tipp wird sichtbar
                        _isFinalHinted.value = false // Anzeige-Logik in UI
                    }
                    switchTurn()
                }
            } catch (_: Exception) {}
        }
        return correct
    }

    /**
     * Skip-Button — gibt Zug ab ohne weitere Aktion.
     */
    fun skipTurn() {
        if (_isSpectator.value || !_isMyTurn.value || _isGameFinished.value) return
        scope.launch { switchTurn() }
    }

    /**
     * Final-Tipp-Button (sichtbar nach 2 falschen Final-Versuchen UND
     * alle 4 Spalten geloest). Zeigt "X _ _ _ Y" als Hint.
     */
    fun useFinalHint() {
        if (_isSpectator.value || _isGameFinished.value) return
        _isFinalHinted.value = true
    }

    /**
     * Aufgeben-Vote setzen. Spiel endet erst, wenn alle Spieler
     * abgestimmt haben (Konsens-Logik aus MainActivity).
     */
    fun voteGiveUp() {
        if (_isSpectator.value || _isGameFinished.value) return
        if (_giveUpVotes.value.contains(playerId)) return
        scope.launch {
            try {
                roomsRef.child(_roomCode.value)
                    .child("giveUpVotes/$playerId")
                    .setValue(true)
            } catch (_: Exception) {}
        }
    }

    /**
     * Neue Runde laden — nur Host. Setzt neue gameId und resetet das Spiel,
     * Scores bleiben erhalten (App-Verhalten in resetGameUI).
     */
    fun loadNewLevel() {
        if (!isHost) {
            _errorMessage.value = "Nur der Host kann ein neues Level laden"
            return
        }
        scope.launch {
            try {
                val pair = levelLoader.fetchRandomLevel(langCode, difficulty)
                if (pair == null) {
                    _errorMessage.value = "Kein Level gefunden"
                    return@launch
                }
                val (newGameId, _) = pair
                lastGameId = null // Erzwingt Neuladen im Listener
                roomsRef.child(_roomCode.value).updateChildren(
                    mapOf(
                        "gameId" to newGameId,
                        "status" to "waiting",
                        "revealedHints" to emptyMap<String, Boolean>(),
                        "solvedColumns" to emptyMap<String, Boolean>(),
                        "giveUpVotes" to emptyMap<String, Boolean>(),
                        "whosTurn" to playerId,
                        "hintUsed" to false,
                        "jokerUsed" to false,
                        "answerUsed" to false,
                        "winner" to ""
                    )
                )
                _finalWrongCount.value = 0
                _isFinalHinted.value = false
                _isAllRevealed.value = false
            } catch (e: Exception) {
                _errorMessage.value = "Fehler: ${e.message}"
            }
        }
    }

    // ───────────────────────────────────────────────────────────
    // Internals
    // ───────────────────────────────────────────────────────────

    private suspend fun switchTurn() {
        val playersList = _players.value
        if (playersList.isEmpty()) return
        val currentIdx = playersList.indexOf(playerId)
        val nextIdx = if (currentIdx >= 0) (currentIdx + 1) % playersList.size else 0
        val nextPlayer = playersList[nextIdx]
        try {
            roomsRef.child(_roomCode.value).updateChildren(
                mapOf(
                    "whosTurn" to nextPlayer,
                    "hintUsed" to false,
                    "jokerUsed" to false,
                    "answerUsed" to false
                )
            )
        } catch (_: Exception) {}
    }

    private suspend fun addOwnScore(delta: Int) {
        val current = _scoresMap.value[playerId] ?: 0
        val newScore = (current + delta).coerceAtLeast(0)
        try {
            roomsRef.child(_roomCode.value).child("scores/$playerId").setValue(newScore)
        } catch (_: Exception) {}
    }

    fun dispose() {
        listenerJob?.cancel()
        // Spieler aus Raum austragen (wie MainActivity.onDestroy)
        scope.launch {
            try {
                val updates = mutableMapOf<String, Any?>(
                    "players/$playerId" to null,
                    "scores/$playerId" to null,
                    "spectators/$playerId" to null
                )
                // Wenn ich am Zug war, Zug an verbleibenden Spieler weitergeben
                val remaining = _players.value.filter { it != playerId }
                if (_isMyTurn.value && remaining.isNotEmpty()) {
                    updates["whosTurn"] = remaining[0]
                    updates["hintUsed"] = false
                    updates["jokerUsed"] = false
                    updates["answerUsed"] = false
                }
                @Suppress("UNCHECKED_CAST")
                roomsRef.child(_roomCode.value).updateChildren(updates as Map<String, Any>)
            } catch (_: Exception) {}
            scope.cancel()
        }
    }
}